// src/products/dataroom/pages/SettingsPage.js
import React, { useState, useEffect } from 'react';
import { Settings, Save, AlertCircle, Loader2, Link2, Copy, Check } from 'lucide-react';
import { supabase } from '../../../config';
import ProjectColorModal from '../components/Modals/ProjectColorModal';
import { cleanUrlName, generateToken, getProjectSlug } from '../utils/slugUtils';

const SettingsPage = ({ projectId, selectedProject, onMessage, onProjectColorChanged, onProjectUpdate }) => {
    const [formData, setFormData] = useState({
        project_name: '',
        description: '',
        url_slug: '',
    });
    const [loading, setLoading] = useState(false);
    const [isColorModalOpen, setIsColorModalOpen] = useState(false);
    const [savedUrl, setSavedUrl] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (selectedProject) {
            setFormData({
                project_name: selectedProject.project_name || '',
                description: selectedProject.description || '',
                url_slug: selectedProject.url_slug || '',
            });
            // Set saved URL if it exists
            if (selectedProject.url_slug && selectedProject.url_token) {
                setSavedUrl(getProjectSlug(selectedProject));
            } else {
                setSavedUrl(null);
            }
        }
    }, [selectedProject]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'url_slug') {
            // Only allow alphanumeric, max 20 chars
            const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20).toLowerCase();
            setFormData(prev => ({ ...prev, [name]: cleaned }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const copyUrlToClipboard = async () => {
        if (savedUrl) {
            const fullUrl = `${window.location.origin}/dealroom/${savedUrl}`;
            try {
                await navigator.clipboard.writeText(fullUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                console.error('Failed to copy:', err);
            }
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.project_name.trim()) {
            onMessage("Project name cannot be empty.", 'error');
            return;
        }

        setLoading(true);
        try {
            const updates = {
                project_name: formData.project_name.trim(),
                description: formData.description.trim(),
                updated_at: new Date().toISOString()
            };

            // Handle URL slug update
            const cleanedUrlSlug = cleanUrlName(formData.url_slug);
            const currentSlug = selectedProject?.url_slug;
            
            if (cleanedUrlSlug) {
                // If slug is new or changed, generate a new token
                if (cleanedUrlSlug !== currentSlug) {
                    // Generate new token for new/changed slug
                    let urlToken = generateToken();
                    
                    // Check if this slug+token combination already exists
                    let attempts = 0;
                    while (attempts < 10) {
                        const { data: existing, error: checkError } = await supabase
                            .from('dataroom_projects')
                            .select('id')
                            .eq('url_slug', cleanedUrlSlug)
                            .eq('url_token', urlToken)
                            .maybeSingle();
                        
                        if (!checkError && !existing) break;
                        if (checkError && checkError.code === 'PGRST116') break;
                        
                        urlToken = generateToken();
                        attempts++;
                    }
                    
                    updates.url_slug = cleanedUrlSlug;
                    updates.url_token = urlToken;
                } else {
                    // Slug hasn't changed - keep existing token (don't update url_token)
                    updates.url_slug = cleanedUrlSlug;
                }
            } else if (formData.url_slug === '' && currentSlug) {
                // User cleared the URL slug - remove it
                updates.url_slug = null;
                updates.url_token = null;
            }
            // If no URL slug entered and none existed, don't update url fields

            const { data, error } = await supabase
                .from('dataroom_projects')
                .update(updates)
                .eq('id', projectId)
                .select()
                .single();

            if (error) throw error;
            
            // Update saved URL display
            if (data.url_slug && data.url_token) {
                setSavedUrl(`${data.url_slug}-${data.url_token}`);
            } else {
                setSavedUrl(null);
            }
            
            // Inform the parent app component to update the project list state
            onProjectUpdate();
            onMessage("Project details updated successfully!", 'success');
        } catch (error) {
            console.error('Error updating project:', error);
            onMessage(`Failed to update project: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!selectedProject) {
        return (
            <div className="p-6 text-center">
                <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Project Selected</h3>
                <p className="text-gray-600">Select a Dataroom project from the sidebar to manage its settings.</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center space-x-3 mb-6">
                <Settings className="h-8 w-8 text-blue-600" />
                <h1 className="text-3xl font-bold text-gray-900">Project Settings</h1>
            </div>
            <p className="text-gray-600 mb-6">
                Manage the core identity and appearance of the **{selectedProject.project_name}** Dataroom.
            </p>

            <div className="bg-white rounded-lg shadow-lg border border-gray-200 divide-y divide-gray-200">
                <form onSubmit={handleSave}>
                    {/* Identity Section */}
                    <div className="p-6 space-y-4">
                        <h2 className="text-xl font-semibold text-gray-900">Project Identity</h2>
                        
                        <div>
                            <label htmlFor="project_name" className="block text-sm font-medium text-gray-700 mb-2">
                                Dataroom Name
                            </label>
                            <input
                                id="project_name"
                                type="text"
                                name="project_name"
                                value={formData.project_name}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., Series A Due Diligence"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={3}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="Briefly describe the purpose of this dataroom."
                            />
                        </div>

                        <div>
                            <label htmlFor="url_slug" className="block text-sm font-medium text-gray-700 mb-2">
                                Dealroom URL Name <span className="text-gray-500 font-normal">(optional)</span>
                            </label>
                            <div className="space-y-2">
                                <input
                                    id="url_slug"
                                    type="text"
                                    name="url_slug"
                                    value={formData.url_slug}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                    placeholder="mydealroom"
                                    maxLength={20}
                                />
                                <p className="text-xs text-gray-500">
                                    Letters and numbers only, max 20 characters. A unique 5-character token will be added automatically.
                                </p>
                                {savedUrl && (
                                    <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                <Link2 className="h-4 w-4 text-green-600" />
                                                <div>
                                                    <p className="text-xs text-gray-600 mb-1">Your Dealroom URL:</p>
                                                    <p className="text-sm font-mono text-green-700 font-semibold">
                                                        /dealroom/{savedUrl}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={copyUrlToClipboard}
                                                className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                                            >
                                                {copied ? (
                                                    <>
                                                        <Check className="h-4 w-4" />
                                                        <span>Copied!</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy className="h-4 w-4" />
                                                        <span>Copy</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Appearance Section */}
                    <div className="p-6 space-y-4">
                        <h2 className="text-xl font-semibold text-gray-900">Appearance</h2>

                        <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border">
                            <div>
                                <h3 className="font-medium text-gray-900">Project Color</h3>
                                <p className="text-sm text-gray-600">
                                    Set a unique color to easily identify this project in the sidebar.
                                </p>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div 
                                    className="w-8 h-8 rounded-full border-2 border-gray-300" 
                                    style={{ backgroundColor: selectedProject.color || '#3B82F6' }}
                                ></div>
                                <button
                                    type="button"
                                    onClick={() => setIsColorModalOpen(true)}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                                >
                                    Change Color
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    {/* Footer / Save Button */}
                    <div className="p-6 flex justify-end bg-gray-50">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-all ${
                                loading
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
                            }`}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" />
                                    Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {isColorModalOpen && (
                <ProjectColorModal
                    isOpen={isColorModalOpen}
                    onClose={() => setIsColorModalOpen(false)}
                    project={selectedProject}
                    onColorChanged={(newColor) => {
                        onProjectColorChanged();
                        setIsColorModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default SettingsPage;